
import React, { useState } from "react";
import useFinancialTransactions from "../../hooks/useFinancialTransactions";
import { TRANSACTION_TYPES } from "../../constants/transactionTypes";
import TransactionForm from "../Revenue/TransactionForm";
import TransactionTable from "../Revenue/TransactionTable";
import styles from "./ExpensesTab.module.css";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

const PAGE_SIZE = 30;

const MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const ExpensesTab = () => {
  const {
    transactions,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    loading,
    error,
    filters,
    setFilters,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  } = useFinancialTransactions(TRANSACTION_TYPES.EXPENSE);

  const [hasApplied, setHasApplied] = useState(false);

  const currentPage = Number(filters?.PageIndex) || 1;

  const goToPage = (page) => {
    if (loading || page < 1 || page > totalPages) return;

    setFilters((prev) => ({
      ...prev,
      PageIndex: page,
      PageSize: PAGE_SIZE,
    }));
  };

  // Form
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Delete
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Date filter
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [filterType, setFilterType] = useState("day");
  const [selectingField, setSelectingField] = useState("From");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tempDate, setTempDate] = useState(null);

  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth()
  );
  const [selectedDay, setSelectedDay] = useState(
    new Date().getDate()
  );

  // Helpers
  const pad = (value) => String(value).padStart(2, "0");

  const formatDisplayDate = (date) => {
    if (!date) return "";

    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()}`;
  };

  const formatApiStart = (date) =>
    `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T00:00:00`;

  const formatApiEnd = (date) =>
    `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T23:59:59`;

  // Form submit
  const handleSubmit = async (data) => {
    if (editingItem) {
      await updateTransaction(editingItem.id, data);
    } else {
      await createTransaction(data);
    }

    setShowForm(false);
    setEditingItem(null);
  };

  // Edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  // Delete
  const handleDelete = (item) => {
    setItemToDelete(item);
  };

  const confirmDelete = async () => {
    if (!itemToDelete?.id) return;

    try {
      setDeleting(true);
      await deleteTransaction(itemToDelete.id);
      setItemToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    if (deleting) return;
    setItemToDelete(null);
  };

  // Open date modal
  const openDateFilter = (field) => {
    setSelectingField(field);

    const currentDate = field === "From" ? fromDate : toDate;
    const dateToUse = currentDate || new Date();

    setTempDate(dateToUse);
    setSelectedYear(dateToUse.getFullYear());
    setSelectedMonth(dateToUse.getMonth());
    setSelectedDay(dateToUse.getDate());
    setShowDateFilter(true);
  };

  // Temporary date selection
  const selectTempDate = (date) => {
    if (!date) return;

    setTempDate(date);
    setSelectedYear(date.getFullYear());
    setSelectedMonth(date.getMonth());
    setSelectedDay(date.getDate());
  };

  // Select day
  const handleSelectDay = (day) => {
    const date = new Date(selectedYear, selectedMonth, day);
    selectTempDate(date);
  };

  // Select month
  const handleSelectMonth = (monthIndex) => {
    const date =
      selectingField === "From"
        ? new Date(selectedYear, monthIndex, 1)
        : new Date(selectedYear, monthIndex + 1, 0);

    selectTempDate(date);
  };

  // Select year
  const handleSelectYear = (year) => {
    const date =
      selectingField === "From"
        ? new Date(year, 0, 1)
        : new Date(year, 11, 31);

    selectTempDate(date);
  };

  // Confirm date selection
  const confirmDateSelection = () => {
    if (!tempDate) return;

    if (selectingField === "From") {
      setFromDate(tempDate);
    } else {
      setToDate(tempDate);
    }

    setShowDateFilter(false);
  };

  // Cancel date modal
  const cancelDateSelection = () => {
    setShowDateFilter(false);
  };

  // Apply API filter
  const applyDateFilter = () => {
    if (!fromDate || !toDate) {
      window.alert("من فضلك اختر التاريخ من وإلى");
      return;
    }

    if (fromDate > toDate) {
      window.alert("تاريخ البداية يجب أن يكون قبل تاريخ النهاية");
      return;
    }

    setFilters((prev) => ({
      ...prev,
      From: formatApiStart(fromDate),
      To: formatApiEnd(toDate),
      PageIndex: 1,
      PageSize: PAGE_SIZE,
    }));

    setHasApplied(true);
  };

  // Clear filter
  const clearDateFilter = () => {
    setFromDate(null);
    setToDate(null);
    setTempDate(null);

    setFilters((prev) => ({
      ...prev,
      From: "",
      To: "",
      PageIndex: 1,
      PageSize: PAGE_SIZE,
    }));

    setHasApplied(false);
  };

  // Calendar
  const daysInMonth = new Date(
    selectedYear,
    selectedMonth + 1,
    0
  ).getDate();

  const currentYear = new Date().getFullYear();

  const years = Array.from(
    { length: 5 },
    (_, index) => currentYear - index
  );

  return (
    <div className={styles.container} dir="rtl">
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h3>المصروفات</h3>
          <p className={styles.subtitle}>
            إدارة ومتابعة المصروفات
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingItem(null);
            setShowForm(true);
          }}
        >
          + إضافة مصروف
        </button>
      </div>

      {/* Date filter */}
      <div className={styles.filterBox}>
        <div className={styles.dateField}>
          <label>من</label>
          <button
            type="button"
            className={styles.dateButton}
            onClick={() => openDateFilter("From")}
          >
            {fromDate
              ? formatDisplayDate(fromDate)
              : "اختر تاريخ البداية"}
          </button>
        </div>

        <div className={styles.dateField}>
          <label>إلى</label>
          <button
            type="button"
            className={styles.dateButton}
            onClick={() => openDateFilter("To")}
          >
            {toDate
              ? formatDisplayDate(toDate)
              : "اختر تاريخ النهاية"}
          </button>
        </div>

        <div className={styles.filterActions}>
          <button
            type="button"
            className={styles.applyButton}
            onClick={applyDateFilter}
          >
            تطبيق
          </button>

          <button
            type="button"
            className={styles.clearButton}
            onClick={clearDateFilter}
          >
            مسح
          </button>
        </div>
      </div>

      {/* Selected range */}
      <div className={styles.selectedRange}>
        <span>الفترة المختارة:</span>

        <strong>
          {fromDate
            ? formatDisplayDate(fromDate)
            : "غير محدد"}

          <span className={styles.arrow}>{" → "}</span>

          {toDate
            ? formatDisplayDate(toDate)
            : "غير محدد"}
        </strong>
      </div>

      {/* Error */}
      {error && (
        <p className={styles.error}>{error}</p>
      )}

      {/* Form */}
      {showForm && (
        <TransactionForm
          initialData={editingItem}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          submitLabel={editingItem ? "تحديث" : "إضافة"}
        />
      )}

      {/* Transactions table and pagination */}
      {hasApplied ? (
        <>
          <TransactionTable
            transactions={transactions}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "12px",
              marginTop: "18px",
              padding: "14px 16px",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              background: "#fff",
            }}
          >
            <span
              style={{
                color: "#4b5563",
                fontSize: "14px",
              }}
            >
              إجمالي الحركات: {totalCount}
              {" — "}
              الصفحة {currentPage} من {totalPages}
              {" — "}
              عدد الحركات في الصفحة: {transactions.length}
            </span>

            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              <button
                type="button"
                disabled={!hasPreviousPage || loading}
                onClick={() => goToPage(currentPage - 1)}
                style={{
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background:
                    !hasPreviousPage || loading
                      ? "#f3f4f6"
                      : "#fff",
                  color:
                    !hasPreviousPage || loading
                      ? "#9ca3af"
                      : "#374151",
                  cursor:
                    !hasPreviousPage || loading
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 600,
                }}
              >
                السابق
              </button>

              <button
                type="button"
                disabled={!hasNextPage || loading}
                onClick={() => goToPage(currentPage + 1)}
                style={{
                  padding: "8px 18px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background:
                    !hasNextPage || loading
                      ? "#f3f4f6"
                      : "#792468",
                  color:
                    !hasNextPage || loading
                      ? "#9ca3af"
                      : "#fff",
                  cursor:
                    !hasNextPage || loading
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 600,
                }}
              >
                التالي
              </button>
            </div>
          </div>
        </>
      ) : (
        <p className={styles.emptyState}>
          اختر الفترة (من - إلى) ثم اضغط "تطبيق" لعرض البيانات
        </p>
      )}

      {/* Delete dialog */}
      <ConfirmDialog
        isOpen={!!itemToDelete}
        title="تأكيد الحذف"
        message={`هل أنت متأكد من حذف "${
          itemToDelete?.title ||
          itemToDelete?.name ||
          "هذا العنصر"
        }"؟ لا يمكن التراجع عن هذا الإجراء.`}
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Date modal */}
      {showDateFilter && (
        <div
          className={styles.modalOverlay}
          onClick={cancelDateSelection}
        >
          <div
            className={styles.modal}
            onClick={(event) => event.stopPropagation()}
          >
            {/* Modal header */}
            <div className={styles.modalHeader}>
              <div>
                <h3>
                  اختيار{" "}
                  {selectingField === "From"
                    ? "تاريخ البداية"
                    : "تاريخ النهاية"}
                </h3>

                <p>
                  اختر اليوم أو الشهر أو السنة ثم اضغط تأكيد
                </p>
              </div>

              <button
                type="button"
                className={styles.closeButton}
                onClick={cancelDateSelection}
              >
                ×
              </button>
            </div>

            {/* Type tabs */}
            <div className={styles.typeTabs}>
              <button
                type="button"
                className={
                  filterType === "day"
                    ? styles.activeTab
                    : ""
                }
                onClick={() => setFilterType("day")}
              >
                يوم
              </button>

              <button
                type="button"
                className={
                  filterType === "month"
                    ? styles.activeTab
                    : ""
                }
                onClick={() => setFilterType("month")}
              >
                شهر
              </button>

              <button
                type="button"
                className={
                  filterType === "year"
                    ? styles.activeTab
                    : ""
                }
                onClick={() => setFilterType("year")}
              >
                سنة
              </button>
            </div>

            {/* Day selection */}
            {filterType === "day" && (
              <>
                <div className={styles.calendarHeader}>
                  <button
                    type="button"
                    onClick={() => {
                      const previous = new Date(
                        selectedYear,
                        selectedMonth - 1,
                        1
                      );

                      setSelectedYear(previous.getFullYear());
                      setSelectedMonth(previous.getMonth());
                    }}
                  >
                    ‹
                  </button>

                  <strong>
                    {MONTHS[selectedMonth]} {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() => {
                      const next = new Date(
                        selectedYear,
                        selectedMonth + 1,
                        1
                      );

                      setSelectedYear(next.getFullYear());
                      setSelectedMonth(next.getMonth());
                    }}
                  >
                    ›
                  </button>
                </div>

                <div className={styles.dayGrid}>
                  {Array.from(
                    { length: daysInMonth },
                    (_, index) => {
                      const day = index + 1;

                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() === selectedYear &&
                        tempDate.getMonth() === selectedMonth &&
                        tempDate.getDate() === day;

                      return (
                        <button
                          key={day}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.dayButton} ${styles.selected}`
                              : styles.dayButton
                          }
                          onClick={() => handleSelectDay(day)}
                        >
                          {day}
                        </button>
                      );
                    }
                  )}
                </div>
              </>
            )}

            {/* Month selection */}
            {filterType === "month" && (
              <>
                <div className={styles.calendarHeader}>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear((prev) => prev - 1)
                    }
                  >
                    ‹
                  </button>

                  <strong>{selectedYear}</strong>

                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear((prev) => prev + 1)
                    }
                  >
                    ›
                  </button>
                </div>

                <div className={styles.monthGrid}>
                  {MONTHS.map((month, index) => {
                    const isSelected =
                      tempDate &&
                      tempDate.getFullYear() === selectedYear &&
                      tempDate.getMonth() === index;

                    return (
                      <button
                        key={month}
                        type="button"
                        className={
                          isSelected
                            ? `${styles.monthButton} ${styles.selected}`
                            : styles.monthButton
                        }
                        onClick={() => handleSelectMonth(index)}
                      >
                        {month}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Year selection */}
            {filterType === "year" && (
              <div className={styles.yearGrid}>
                {years.map((year) => {
                  const isSelected =
                    tempDate &&
                    tempDate.getFullYear() === year;

                  return (
                    <button
                      key={year}
                      type="button"
                      className={
                        isSelected
                          ? `${styles.yearButton} ${styles.selected}`
                          : styles.yearButton
                      }
                      onClick={() => handleSelectYear(year)}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Temporary selection and modal actions */}
            <div className={styles.modalFooter}>
              <div className={styles.tempSelectedDate}>
                <span>الاختيار الحالي:</span>

                <strong>
                  {tempDate
                    ? formatDisplayDate(tempDate)
                    : "لم يتم الاختيار"}
                </strong>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelModalButton}
                  onClick={cancelDateSelection}
                >
                  إلغاء
                </button>

                <button
                  type="button"
                  className={styles.confirmModalButton}
                  disabled={!tempDate}
                  onClick={confirmDateSelection}
                >
                  تأكيد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesTab;
